/**
 * 微信支付自定义Hook
 * 用于管理微信支付的状态和逻辑
 */

import { useState, useCallback } from 'react';
import { message } from 'antd';
import { WxOrderInfo } from '@/types/payment';
import { invokeWxPay, isSupportWxPay, isWeiXin } from '@/utils/wxpay';

export interface UseWxPayOptions {
  onSuccess?: () => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
}

export interface UseWxPayReturn {
  paying: boolean;
  callWxPay: (orderInfo: WxOrderInfo) => Promise<void>;
  isWxPaySupported: boolean;
}

export function useWxPay(options: UseWxPayOptions = {}): UseWxPayReturn {
  const { onSuccess, onCancel, onError } = options;
  const [paying, setPaying] = useState(false);

  const isWxPaySupported = isWeiXin() && isSupportWxPay();

  const callWxPay = useCallback(async (orderInfo: WxOrderInfo) => {
    try {
      // 检查是否在微信环境中
      if (!isWeiXin()) {
        const errorMsg = '请在微信中打开此页面';
        message.error(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // 检查是否支持微信支付
      if (!isSupportWxPay()) {
        const errorMsg = '微信支付环境未准备就绪';
        message.error(errorMsg);
        onError?.(errorMsg);
        return;
      }

      setPaying(true);
      console.log('开始调起微信支付:', orderInfo);

      // 直接调起微信支付
      await invokeWxPay(orderInfo);
      
      // 支付成功
      console.log('微信支付成功');
      onSuccess?.();

    } catch (error: unknown) {
      console.error('微信支付失败:', error);
      
      const errorMessage = error instanceof Error ? error.message : '支付失败';
      
      if (errorMessage === '用户取消支付') {
        console.log('用户取消支付');
        onCancel?.();
      } else {
        message.error(errorMessage || '支付失败，请重试');
        onError?.(errorMessage);
      }
    } finally {
      setPaying(false);
    }
  }, [onSuccess, onCancel, onError]);

  return {
    paying,
    callWxPay,
    isWxPaySupported,
  };
} 